import { Fragment, useState } from 'react';
import Button from 'src/components/buttons/button';
import NumberedList from 'src/components/numberedList';
import config from 'config/config';
import Icon from 'src/components/icon';

const PROGRAM_URL = `${config.cdn.navigator}lower-your-cholesterol/`;

const getMenu = () => (
  <section className="navigator__contents">
    <div className="navigator__contents-header">Contents</div>
    <ul className="navigator__contents__list">
      <li>
        <div className="navigator__contents__number">1.</div>
        <div className="navigator__contents__content-text">
          <a href="#1">Your weight and cholesterol</a>
        </div>
      </li>
      <li>
        <div className="navigator__contents__number">2.</div>
        <div className="navigator__contents__content-text">
          <a href="#2">{`Finding out if you're a healthy weight`}</a>
        </div>
      </li>
      <li>
        <div className="navigator__contents__number">3.</div>
        <div className="navigator__contents__content-text">
          <a href="#3">Myths about weight gain</a>
        </div>
      </li>
      <li>
        <div className="navigator__contents__number">4.</div>
        <div className="navigator__contents__content-text">
          <a href="#4">{`Shirley's story`}</a>
        </div>
      </li>
    </ul>
  </section>
);

const personalisation_mapping = {
  k1: {
    v1: 'UNDER_30',
    v2: '30_55',
    v3: 'OVER_55',
  },
  k2: {
    v1: 'WHITE',
    v2: 'BLACK_ALL',
    v3: 'ASIAN_ALL',
    v4: 'MIXED',
    v5: 'OTHER',
  },
  k3: {
    v1: 'YES',
    v2: 'NO',
  },
  k4: {
    v1: 'YES',
    v2: 'NO',
  },
  c1: {
    v1: 'YES',
    v2: 'NO',
  },
};

const getPage = personalisation => {
  const { k2 } = personalisation;
  const ethnicity = personalisation_mapping.k2[k2];

  let personalisedSection = '';
  if (ethnicity === 'BLACK_ALL') {
    personalisedSection = 'AC';
  } else {
    personalisedSection = 'DEFAULT';
  }

  return (
    <div className="navigator">
      <section className="navigator__content">
        <h1 className="navigator__title">{'Week 8: How weight affects cholesterol'}</h1>
        <p>
          {`Being a healthy weight can help protect you from heart and circulatory diseases in the future. This week we'll
          help you find out what's meant by a 'healthy' weight and how to start taking steps to getting there if you
          would benefit from losing weight.`}
        </p>
        <div className="visible-xs">{getMenu()}</div>

        {personalisedSection === 'DEFAULT' && (
          <div>
            <h2>Your weight and cholesterol</h2>
            <p>
              {`If you are overweight, you are more likely to have high cholesterol which means you're more likely to
              develop heart and circulatory diseases in the future. And too much fat around the waist makes people more
              likely to develop conditions like Type 2 diabetes.`}
            </p>
            <p>
              Your genes will have a say in where you carry fat, and some people can carry too much fat around their
              middle even if they are not overweight. But the choices you make around eating and exercise will play the
              biggest role in whether you are overweight or not.
            </p>
            <p>
              {`A lot of people don't realise that even though they feel fine, their weight is putting their health at
              risk. This is a lot to come to terms with, but the good news is that there are things you can do to lose
              the weight gradually.`}
            </p>
            <a
              href="https://www.bhf.org.uk/heart-health/risk-factors/your-weight-and-heart-disease"
              rel="noreferrer"
              target="_blank"
            >
              <Button
                icon="open-right"
                iconLeftAligned={false}
                text={'Read more about weight'}
              />
            </a>
          </div>
        )}

        {personalisedSection === 'AC' && (
          <div>
            <h2>Your weight and cholesterol</h2>
            <p>
              {`If you are of African or African Caribbean descent, you carry a greater risk of having high blood pressure
              or a stroke than other ethnic groups in the UK. And being overweight can increase that risk even more, so
              it's important for you to maintain a healthy weight.`}
            </p>
            <p>
              Your weight could be putting your health at risk, even if you feel fine. This is a big thing to come to
              terms with, but the good news is that there are things you can do to lose the weight gradually and bring
              your blood pressure down.
            </p>
            <a
              href="https://www.bhf.org.uk/heart-matters-magazine/medical/african-caribbean-background-and-heart-health"
              rel="noreferrer"
              target="_blank"
            >
              <Button
                icon="open-right"
                iconLeftAligned={false}
                text={'Read more about weight'}
              />
            </a>
          </div>
        )}

        <h2>{`Finding out if you're a healthy weight`}</h2>
        <p>
          To find out if you should lose weight, measure your Body Mass Index (BMI) and your waist. If either
          measurement is higher than it should be, speak to your GP or nurse about how to lose weight gradually.
        </p>
        <p>
          {`You can use a simple BMI calculator to find out if you're a healthy weight for your height. You just
          need to know how tall you are and how much you weigh.`}
        </p>
        <div style={{ marginBottom: '8px' }}>
          <a
            href="https://extras.bhf.org.uk/patientinfo/bmi-v1.01/app/index.html"
            rel="noreferrer"
            target="_blank"
          >
            <Button
              icon="open-right"
              iconLeftAligned={false}
              text={'Calculate your BMI'}
            />
          </a>
        </div>
        <p>
          To measure your waist, wrap a tape measure around your middle between the bottom of your ribs and the top of
          your hips, just above your belly button. The tape measure should fit snugly - try not to tense or breathe in.
        </p>
        <p>
          Alternately, click the link below to view a guide on how to do so.
        </p>
        <div style={{ marginBottom: '8px' }}>
          <a
            href="https://extras.bhf.org.uk/patientinfo/waist-measurement_v1.0/app/"
            rel="noreferrer"
            target="_blank"
          >
            <Button
              icon="open-right"
              iconLeftAligned={false}
              text={'Measure your waist'}
            />
          </a>
        </div>
        <img alt="Measuring tape" className="navigator__image-full" src={`${PROGRAM_URL}W08B02.jpg`} />

        <h2>Myths about weight gain</h2>
        <p>3 myths we tell ourselves about our weight:</p>
        <NumberedList
          items={[
            <Fragment key="1">
              <strong>{`We're all getting bigger, it's natural.`}</strong>{' '}{`Because obesity is rising in the UK, we've become
              used to people being larger. But the health risks are the same as they always have been.`}
            </Fragment>,
            <Fragment key="2">
              <strong>I only have a bit of a belly.</strong>{' '}{`Storing excess fat around your middle increases your risk
              of heart and circulatory diseases. This fat is called visceral fat - it builds up around your internal
              organs and too much of it is dangerous for your health, even if you're a healthy weight.`}
            </Fragment>,
            <Fragment key="3">
              <strong>{`Eating the odd biscuit won't make me overweight.`}</strong> Snacks that seem small can still add up
              to a lot of calories. For example, eating two custard creams a day (an extra 120 kcals a day) on top of
              your normal diet can increase your weight by 12 lb or 5kg in one year.
            </Fragment>,
          ]}
        />
        <a
          href="https://www.bhf.org.uk/heart-matters-magazine/nutrition/weight/obesity/weight-gain-myths"
          rel="noreferrer"
          target="_blank"
        >
          <Button
            icon="open-right"
            iconLeftAligned={false}
            text={'Top weight loss facts'}
          />
        </a>

        <h2>{`Shirley's story`}</h2>
        <p>
          {`Losing weight isn't always easy, but it's definitely possible. You can do it, even if you have struggled with
          your weight for a long time. Find out how Shirley's small, consistent changes added up to make her feel
          healthier and happier.`}
        </p>
        <div className="navigator__video-container">
          <iframe
            allow="autoplay; encrypted-media; fullscreen"
            src="https://www.youtube.com/embed/4WmDzwYonpY"
          />
        </div>

        <img alt="Content provided by BHF" className="navigator__image-full navigator__banner-footer" src={`${PROGRAM_URL}bhf-content.jpg`} />
      </section>
    </div>
  );
};

function Footer() {
  const [visible, setVisible] = useState(true);

  if (!visible) return null;
  return (
    <div className="navigator__footer">
      <img alt="HealthUnlocked British Heart Foundation community screenshot" className="navigator__footer__image" src={`${PROGRAM_URL}cta-community.png`} />
      <div className="navigator__footer__inner">
        <div className="navigator__footer__title">Join the conversation on HealthUnlocked</div>
        <div className="navigator__footer__description">
          {`To get the most out of your heart health journey, you'll need support. Our online community on
            HealthUnlocked brings patients, their loved-ones and healthcare experts all together online to share advice
            and encouragement.`}{' '}
          <a
            className="navigator__footer__link"
            href="https://healthunlocked.com/bhf"
            rel="noreferrer"
            target="_blank"
          >
            Join the conversation {'>'}
          </a>
        </div>
        <div className="navigator__footer__close" onClick={() => setVisible(false)}>
          <Icon icon="cross" />
        </div>
      </div>
    </div>
  );
}

const obj = {
  getPage,
  getMenu,
  Footer,
  getSeoTitle: 'Lower your cholesterol | HealthUnlocked',
};

export default obj;
